
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getEndpointUrl } from '@/lib/api-config';
import { useAuth } from '@/contexts/AuthContext';

export type ActivationKey =
  | 'conexionSii'
  | 'credencialesAdquirente'
  | 'scrapeCuentas'
  | 'firmaPagare'
  | 'cuentaBancaria'
  | 'terminos'
  | 'lupsActivation';

export interface ActivationStepMeta {
  key: ActivationKey;
  href: string;
  icon: string;
  title: string;
  subtitle: string;
  /** Si es false, el paso se muestra como informativo y no cuenta para completitud. */
  required?: boolean;
}

/**
 * Orden del wizard según el plan maestro (Fase 4.1):
 *   SII → credenciales adquirente → scrape cuentas (triggered) → cobros automáticos (TC obligatoria)
 *   → firma pagaré → cuenta bancaria → términos.
 *
 * `scrapeCuentas` es informativo (se dispara solo tras credenciales) pero NO bloqueante
 * para poder pasar al siguiente paso: el comercio puede avanzar mientras el scrapper
 * corre en background.
 */
export const ACTIVATION_STEPS: ActivationStepMeta[] = [
  {
    key: 'conexionSii',
    href: '/configuraciones/conexion-sii',
    icon: 'receipt_long',
    title: 'Conexión SII',
    subtitle: 'Facturación electrónica',
    required: true,
  },
  {
    key: 'credencialesAdquirente',
    href: '/configuraciones/credenciales-adquirente',
    icon: 'key',
    title: 'Credenciales Adquirente',
    subtitle: 'Origen de tus ventas',
    required: true,
  },
  {
    key: 'lupsActivation',
    href: '/configuraciones/pago-automatico-tarjetas',
    icon: 'credit_card',
    title: 'Pago automático con tarjeta (PAT)',
    subtitle: 'Inscribe tu tarjeta de crédito',
    required: true,
  },
  {
    key: 'scrapeCuentas',
    href: '/configuraciones/pago-automatico-cuentas',
    icon: 'account_balance',
    title: 'Pago automático de cuentas (PAC)',
    subtitle: 'Mandato bancario para cobro automático',
    required: false,
  },
  {
    key: 'firmaPagare',
    href: '/configuraciones/firma-pagare',
    icon: 'gavel',
    title: 'Firma de Pagaré',
    subtitle: 'Respaldo financiero',
    required: true,
  },
  {
    key: 'cuentaBancaria',
    href: '/configuraciones/cuenta-bancaria',
    icon: 'account_balance',
    title: 'Cuenta Bancaria',
    subtitle: 'Para recibir fondos',
    required: true,
  },
  {
    key: 'terminos',
    href: '/configuraciones/terminos',
    icon: 'description',
    title: 'Términos y Condiciones',
    subtitle: 'Aceptación de uso',
    required: true,
  },
];

type ActivationStepEntry = { completedAt: string } | null;
type LupsActivationEntry = { tc: 'pending' | 'complete'; completedAt: string | null };
type ScrapeCuentasEntry = { status: 'pending' | 'triggered' | 'complete'; detectedCount: number; completedAt: string | null };

type ActivationStatusResponse = {
  conexionSii: ActivationStepEntry;
  credencialesAdquirente: ActivationStepEntry;
  scrapeCuentas: ScrapeCuentasEntry;
  firmaPagare: ActivationStepEntry;
  cuentaBancaria: ActivationStepEntry;
  terminos: ActivationStepEntry;
  lupsActivation: LupsActivationEntry;
  adquirentes?: Record<string, { activatedAt: string }>;
};

interface ActivationContextValue {
  isLoading: boolean;
  status: ActivationStatusResponse | null;
  completedCount: number;
  pendingCount: number;
  isStepComplete: (key: ActivationKey) => boolean;
  isAcquirerActivated: (slug: string) => boolean;
  refreshStatus: () => Promise<void>;
}

const REQUIRED_STEP_COUNT = ACTIVATION_STEPS.filter((s) => s.required).length;

const ActivationContext = createContext<ActivationContextValue>({
  isLoading: true,
  status: null,
  completedCount: 0,
  pendingCount: REQUIRED_STEP_COUNT,
  isStepComplete: () => false,
  isAcquirerActivated: () => false,
  refreshStatus: async () => {},
});

export function ActivationProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<ActivationStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const url = getEndpointUrl('activation', 'status');
      const { httpClient } = await import('@/lib/http-client');
      const data = await httpClient.get<ActivationStatusResponse>(url, { shouldCache: false });
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.emailVerified) {
      setStatus(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchStatus();
  }, [user, authLoading]);

  const refreshStatus = async () => {
    setIsLoading(true);
    await fetchStatus();
  };

  const isStepComplete = (key: ActivationKey) => {
    if (!status) return false;
    if (key === 'lupsActivation') return status.lupsActivation?.tc === 'complete';
    if (key === 'scrapeCuentas') return status.scrapeCuentas?.status === 'complete';
    const entry = status[key] as ActivationStepEntry;
    return !!entry?.completedAt;
  };
  const isAcquirerActivated = (slug: string) => !!status?.adquirentes?.[slug]?.activatedAt;

  const requiredSteps = ACTIVATION_STEPS.filter((s) => s.required);
  const completedCount = status ? requiredSteps.filter((s) => isStepComplete(s.key)).length : 0;
  const pendingCount = requiredSteps.length - completedCount;

  return (
    <ActivationContext.Provider value={{ isLoading, status, completedCount, pendingCount, isStepComplete, isAcquirerActivated, refreshStatus }}>
      {children}
    </ActivationContext.Provider>
  );
}

export function useActivation() {
  return useContext(ActivationContext);
}
